<template>
    <div class="tenant-selector" :class="{ readonly: readonly }">
        <div class="tenant-label">{{ $t('navBar.tenant') }}</div>
        <v-select
            v-model="currentTenant"
            :items="tenants"
            item-title="name"
            item-value="key"
            hide-details
            :readonly="readonly"
            single-line
            :menu-icon="null"
            placeholder="Select Tenant"
            class="rtls-select"
            @update:model-value="setTenant"
        />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { isEmbedded } from '@/js/webshell-embed/embedded';

defineOptions({ name: 'TenantSelector' });

const store = useStore();

const currentTenant = ref(store.getters['context/getCurrentTenant']);

const tenants = computed(() => store.getters['context/getAvailableTenants'] || []);

// Automatically set readonly based on number of available tenants
const readonly = computed(() => tenants.value.length <= 1);

// Almost every page is tenant-scoped, so reload the home page rather than switching in place:
// a full reload guarantees no state from the previous tenant survives.
const setTenant = tenantKey => {
    // Embedded, the tenant is part of the shell URL - ADR-004 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/004-tenant-in-url-path.md
    // and changing tenants triggers a full reload of the browser page
    if (isEmbedded()) throw new Error('the shell owns tenant switching while embedded');

    if (!tenantKey || tenantKey === store.getters['context/getCurrentTenant']) return;

    // Persist the choice before leaving, so it survives even if the query param does not
    // (e.g. an auth redirect on the way back in).
    store.dispatch('context/setCurrentTenant', tenantKey);

    window.location.assign(`/?tenant=${encodeURIComponent(tenantKey)}`);
};
</script>

<style lang="scss" scoped>
:deep(.v-input__append-inner) {
    transform: rotate(-90deg);
}

.tenant-label {
    font-size: 1.2rem;
    font-weight: normal;
    padding: 17px;
}

.tenant-selector {
    display: flex;
    align-items: center;
    font-size: 1.2rem;

    &.readonly {
        opacity: 0.8;

        :deep(.v-input__append-inner) {
            display: none; // Hide dropdown arrow for readonly
        }
    }
}
</style>
